import { getCookie, setCookie } from '../cookies';
import Offcanvas from 'bootstrap/js/dist/offcanvas';
import { toggleTheme } from '../theme-toggle';
import { togglePresentationMode } from '../cursor';
import { toggleLineType } from '../line-type';
import { toggleVerticalGridLines } from '../vertical-grid-lines';
import { updateLineStyle } from '../line-style';
import { toggleRegionalStatisticsSingleModelColor } from '../charts/regional-statistics-single-model';
import { map, ts } from '../../modules/main';

// import interfaces
import { Aeroval } from '../../types/global';
import { CanvasConfig } from '../../types/config';
declare var aeroval: Aeroval;

import canvasConfig from '../../../public/config/canvas.json';

import './style.css'

type LineStyle = 'lineWidthFactor' | 'markerEnabledThreshold' | 'markerSize';

function getStoredSettingValue(settingId: string, defaultValue: string | number) {
	return getCookie(settingId) ? getCookie(settingId) : defaultValue;
}

function getLineStyleSetting(settingId: string): LineStyle | null {
	if (settingId === 'lineWidthFactor') {
		return 'lineWidthFactor';
	}

	if (settingId === 'markerThreshold') {
		return 'markerEnabledThreshold';
	}

	return null;
}

function applyDefaultRadioSetting(settingId: string, value: string) {
	if (settingId === 'theme') {
		toggleTheme(value as Parameters<typeof toggleTheme>[0]);
	}

	if (settingId === 'presentationMode') {
		togglePresentationMode(value);
	}

	if (settingId === 'lineType') {
		toggleLineType(value);
	}

	if (settingId === 'verticalGridLines') {
		toggleVerticalGridLines(value);
	}
}

function applyChangedRadioSetting(settingId: string, value: string) {
	if (settingId === 'theme') {
		toggleTheme(value as Parameters<typeof toggleTheme>[0]);
	}

	if (settingId === 'presentationMode') {
		togglePresentationMode(value);
	}

	if (settingId === 'lineType') {
		toggleLineType(value);
	}

	if (settingId === 'verticalGridLines') {
		toggleVerticalGridLines(value);
	}

	if (settingId === 'regionalStatisticsSingleModelColor') {
		toggleRegionalStatisticsSingleModelColor(value);
	}

	if (settingId === 'showEmptyRegions') {
		map(true, false, false);
	}

	if (settingId === 'showProxyVariables') {
		ts(true, true);
	}

	if (settingId === 'showIndividualModels') {
		map(true, true, true);
	}

	if (settingId === 'zoomOnSelectedRegion') {
		map(false, false, false);
	}

	if (settingId === 'zoomOnSelectedSeason') {
		ts(true, false);
	}
}

function applyRangeSetting(settingId: string, value: string | number) {
	const lineStyleSetting = getLineStyleSetting(settingId);

	if (lineStyleSetting) {
		updateLineStyle(lineStyleSetting, String(value));
	}
}

export function offCanvas(categoriesFilter: string[] | undefined = undefined) {
	// skip if already rendered
	if (document.querySelector('#offcanvas-settings')) return;
	fillOutOffCanvasContent(canvasConfig as any, categoriesFilter);
}

function fillOutOffCanvasContent(data: CanvasConfig, categoriesFilter: string[] | undefined = undefined) {
	const activeSettings = [] as any[];
	const theme = aeroval.settings.theme ? aeroval.settings.theme : 'light';
	const offCanvasHeader = `<div class="offcanvas offcanvas-end bg-${theme} bg-opacity-75" tabindex="-1" id="offcanvas-settings" aria-labelledby="offcanvas-settings-label">
        <div class="offcanvas-header">
            <h5 class="offcanvas-title" id="offcanvas-settings-label">
                <i class="fas fa-gear"></i>
                Users Settings
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
        </div>`;

	let offCanvasBody = `<div class="offcanvas-body py-0 px-4">`;
	// first level: categories
	for (let category of (data as any)) {
		// skip category if category filter and category not in the list
		if (categoriesFilter && !categoriesFilter.includes(category.id)) continue;
		// add category title
		offCanvasBody += `<h5 class="offcanvas-title mt-4" id="offcanvas-${category.id}-label" title="${category.description}">${category.title}</h5>`
		// second level: settings
		for (let setting of category.options) {
			// skip setting if setting enabled is false
			if (setting.enabled === false) continue;
			activeSettings.push(setting);

			offCanvasBody += `<h6 class="offcanvas-title" id="offcanvas-${category.id}-label" title="${setting.description}">${setting.title}</h6>`

			if (setting.type === 'radio') {
				offCanvasBody += `<div class="btn-group" role="group" aria-label="Basic radio toggle button group">`;
				for (let option of setting.options) {
					offCanvasBody += `<input type="radio" class="btn-check" name="btn-radio-${setting.id}" id="btn-radio-${setting.id}-${option.value}" autocomplete="off">
					<label class="btn btn-${theme}" for="btn-radio-${setting.id}-${option.value}" value="${option.value}">${option.title}</label>`;
				}
				offCanvasBody += `</div>`;
			} else if (setting.type === 'range') {
				offCanvasBody += `<div style="display: inline-flex; align-items: center;">
				<input type="range" id="range-${setting.id}" name="range-${setting.id}" min="${setting.options[0].value}" max="${setting.options[1].value}" step="${setting.options[2].value}" value="${setting.default}" oninput="document.getElementById('range-${setting.id}-output').value = this.value">
				<output id="range-${setting.id}-output" class="mx-3">${setting.default} (default: ${setting.default})</output>
			</div>`;
			}
		}
	}
	offCanvasBody += `</div>`;

	// append directly to body (same pattern as heatmap/map offcanvas)
	const wrapper = document.createElement('div');
	wrapper.innerHTML = offCanvasHeader + offCanvasBody + `</div>`;
	document.body.appendChild(wrapper.firstElementChild!);

	const offcanvasElement = document.getElementById('offcanvas-settings');
	if (!offcanvasElement) return;

	// strip data-bs-toggle from all trigger buttons so Bootstrap never auto-toggles,
	// then bind an explicit show() on each click
	document.querySelectorAll('[data-bs-target="#offcanvas-settings"]').forEach((btn) => {
		if (!(btn instanceof HTMLElement)) return;
		btn.removeAttribute('data-bs-toggle');
		btn.removeAttribute('data-bs-target');
		btn.addEventListener('click', () => {
			new Offcanvas(offcanvasElement).show();
		});
	});

	for (const setting of activeSettings) {
		const defaultOption = getStoredSettingValue(setting.id as string, setting.default);
		const defaultValue = String(defaultOption);

		if (setting.type === 'radio') {
			const defaultElement = document.querySelector(`#btn-radio-${setting.id as string}-${defaultValue}`);
			if (defaultElement instanceof HTMLInputElement) {
				defaultElement.checked = true;
				applyDefaultRadioSetting(setting.id as string, defaultValue);
			}
		} else if (setting.type === 'range') {
			const rangeElement = document.querySelector(`input[id=range-${setting.id as string}]`);
			if (rangeElement instanceof HTMLInputElement) {
				rangeElement.value = defaultValue;
			}

			const rangeOutputElement = document.querySelector(`#range-${setting.id as string}-output`);
			if (rangeOutputElement instanceof HTMLOutputElement) {
				rangeOutputElement.innerHTML = defaultValue;
			}

			applyRangeSetting(setting.id as string, defaultValue);
		}

		aeroval.settings[setting.id as string] = defaultValue;
	}

	for (const setting of activeSettings) {
		document.querySelectorAll(`input[name=btn-radio-${setting.id}]`).forEach((element) => {
			element.addEventListener('change', function (event) {
				const inputElement = event.target as HTMLInputElement;
				const value = inputElement.id.split(`btn-radio-${setting.id}-`)[1];
				aeroval.settings[setting.id as string] = value;
				setCookie(setting.id as string, value, 365);
				applyChangedRadioSetting(setting.id as string, value);
			});
		});

		const rangeElement = document.querySelector(`input[id=range-${setting.id as string}]`);
		if (rangeElement instanceof HTMLInputElement) {
			rangeElement.addEventListener('change', function (event) {
				const value = (event.target as HTMLInputElement).value;
				aeroval.settings[setting.id as string] = value;
				setCookie(setting.id as string, value, 365);
				applyRangeSetting(setting.id as string, value);
			});
		}
	}

}
